import { Injectable } from '@angular/core';
import { ApiService } from 'src/app/common/services/api/api.service';
import {
  CreateItineraryCommand,
  Itinerary,
  ItineraryAttraction,
  ItineraryDay,
  ItinerarySettings,
  MultiWidgetSettings,
} from '../models/itinerary';
import { Observable, map, BehaviorSubject, lastValueFrom } from 'rxjs';
import { EventTimeService } from '../../event-calendar/services/event.time.service';
import { VisitorEvent } from '../../shared/models/visitor.event';
import { VisitorPlaceService } from '../../event-calendar/services/visitor.place.service';
import { VisitorPlace } from '../../shared/models/visitor.place';
import {
  TripStorageCommand,
  UserTrip,
  UserTripModel,
} from '../../shared/models/vititor.trip.storage.command';
import { isEmptyArray } from '../../../common/utils/object.extensions';
import { VisitorService } from '../../shared/services/visitor.service';
import { TravelMode } from '../../shared/services/google.map.service';

@Injectable({
  providedIn: 'root',
})
export class TripPlannerService {
  public static modalClose$ = new BehaviorSubject<any>(null);
  public static itinerariesSubject$ = new BehaviorSubject<Itinerary[]>(null);

  static planTypes = [
    {
      type: 'Custom',
      textColor: '#878787',
      bgColor: '#e9e9e9',
      iconColor: '#c6c6c6',
      icon: 'star',
    },
    {
      type: 'Flight',
      textColor: '#b794f4',
      bgColor: '#e6e6fb',
      iconColor: '#b8b8fd',
      icon: 'flight_takeoff',
    },
    {
      type: 'Train',
      textColor: '#ff9494',
      bgColor: '#ffe4e4',
      iconColor: 'pink',
      icon: 'train',
    },
    {
      type: 'Bus',
      textColor: '#df7ffb',
      bgColor: '#f9e5ff',
      iconColor: '#f1c0ff',
      icon: 'directions_bus_filled',
    },
    {
      type: 'Subway',
      textColor: '#ff9569',
      bgColor: '#fdecd9',
      iconColor: '#ffbf8c',
      icon: 'subway',
    },
    {
      type: 'Taxi / Car',
      textColor: '#37a3ff',
      bgColor: '#cbe8ff',
      iconColor: '#aad5ff',
      icon: 'local_taxi',
    },
    {
      type: 'Visit',
      textColor: '#50a7c7',
      bgColor: '#cfe9f3',
      iconColor: '#99d7ee',
      icon: 'tour',
    },
    {
      type: 'Explore',
      textColor: '#4eb284',
      bgColor: '#daebe3',
      iconColor: '#bedacd',
      icon: 'explore',
    },
    {
      type: 'Stroll',
      textColor: '#c0823e',
      bgColor: '#ebdbca',
      iconColor: '#f3c89a',
      icon: 'directions_walk',
    },
    {
      type: 'Wander',
      textColor: '#c0823e',
      bgColor: '#ebdbca',
      iconColor: '#f3c89a',
      icon: 'directions_walk',
    },
    {
      type: 'Dine',
      textColor: '#668af5',
      bgColor: '#dbe4ff',
      iconColor: '#c3d2ff',
      icon: 'restaurant',
    },
    {
      type: 'Dining',
      textColor: '#668af5',
      bgColor: '#dbe4ff',
      iconColor: '#c3d2ff',
      icon: 'restaurant',
    },
    {
      type: 'Breakfast',
      textColor: '#02be02',
      bgColor: '#ddf5ea',
      iconColor: '#8bd78b',
      icon: 'fastfood',
    },
    {
      type: 'Lunch',
      textColor: '#db9a29',
      bgColor: '#ffe7be',
      iconColor: '#fbcb77',
      icon: 'lunch_dining',
    },
    {
      type: 'Dinner',
      textColor: '#ff8f1b',
      bgColor: '#f6eca7',
      iconColor: '#ffdf53',
      icon: 'local_dining',
    },
  ];

  static sendData(data: { type: string; data: any }) {
    TripPlannerService.modalClose$.next(data);
  }

  constructor(
    private _apiService: ApiService,
    private _eventTimeService: EventTimeService,
    private _visitorService: VisitorService,
  ) {}

  getAllItineraries(hubName: string): Observable<Itinerary[]> {
    return this._apiService.get(`hubs/${hubName}/tours`);
  }

  getActiveItineraries(hubName: string): Observable<Itinerary[]> {
    return this._apiService.get(`visitors/${hubName}/tours`);
  }

  getItineraryDetails(hubName: string, id: string): Observable<Itinerary> {
    return this._apiService.get(`hubs/${hubName}/tours/${id}/true`).pipe(
      map((itinerary: Itinerary) => {
        // Stored/API duration is minutes; the newer UI formats seconds.
        itinerary.travelTime = (itinerary.travelTime || 0) * 60;
        itinerary.attractions.forEach((stop) => {
          if (stop.feedType === 'event') {
            this._eventTimeService.processEventTimes(stop.item as VisitorEvent);
          } else {
            stop.item.address = {
              address: stop.item.address.toString(),
              lat: stop.item.lat,
              lng: stop.item.lng,
            };
            (stop.item as VisitorPlace).categoryText = this._visitorService.getDisplayCategory(
              stop.item as VisitorPlace,
            );
          }
        });

        if (itinerary.days) {
          itinerary.days.forEach((day) => {
            day.attractions?.forEach((attraction) => {
              if (day.attractions) {
                day.attractions = day.attractions.filter(
                  (attraction) => attraction.status === 'ok',
                );
                if (attraction.feedType === 'event') {
                  this._eventTimeService.processEventTimes(attraction.item as VisitorEvent);
                } else {
                  attraction.item.address = {
                    address: attraction.item.address.toString(),
                    lat: attraction.item.lat,
                    lng: attraction.item.lng,
                  };
                  (attraction.item as VisitorPlace).categoryText =
                    this._visitorService.getDisplayCategory(attraction.item as VisitorPlace);
                }
              }
            });
          });
        }

        return itinerary;
      }),
    );
  }

  createItinerary(command: CreateItineraryCommand): Observable<Itinerary> {
    return this._apiService.post(`hubs/${command.hubName}/tours`, command);
  }

  updateItinerary(id: string, command: CreateItineraryCommand): Observable<Itinerary> {
    return this._apiService.put(`hubs/${command.hubName}/tours/update/${id}`, command);
  }

  updateItineraryDays(
    hubName: string,
    id: string,
    days: any[], //ItineraryDay[]
    descriptionChangedAttractionIds?: string[],
    defaultTravelMode?: Itinerary['defaultTravelMode'],
  ): Observable<Itinerary> {
    return this._apiService.put(`hubs/${hubName}/tours/update/${id}/days`, {
      command: days,
      descriptionChangedAttractionIds,
      defaultTravelMode,
    });
  }

  deleteItinerary(id: string, hubName: string): Observable<any> {
    return this._apiService.delete(`hubs/${hubName}/tours/${id}`);
  }

  updateItineraryAttractions(
    hubName: string,
    id: string,
    command: ItineraryAttraction[],
    descriptionChangedAttractionIds: string[],
  ): Observable<Itinerary> {
    return this._apiService.put(`hubs/${hubName}/tours/update/${id}/attractions`, {
      command,
      descriptionChangedAttractionIds,
    });
  }

  updateSettings(
    hubName: string,
    widgetCode: string,
    settings: ItinerarySettings,
  ): Observable<Itinerary> {
    return this._apiService.put(`hubs/${hubName}/tours/update-settings`, {
      widgetCode,
      settings,
    });
  }
  updateMultiWidgetSettings(
    hubName: string,
    settings: MultiWidgetSettings[],
  ): Observable<Itinerary> {
    return this._apiService.put(`hubs/${hubName}/tours/update-multi-widget-ettings`, settings);
  }

  getMultiWidgetSettings(hubName: string): Observable<MultiWidgetSettings[]> {
    return this._apiService.get(`hubs/${hubName}/tours/get-multi-widget-settings`);
  }

  moveItineraryToWidgets(
    hubName: string,
    itineraryId: string,
    widgetCodes: string[],
  ): Observable<any> {
    return this._apiService.put(`hubs/${hubName}/tours/assign-to-multi-widgets/${itineraryId}`, {
      widgetCodes,
    });
  }

  updateDetailsStyleForAll(
    hubName: string,
    detailsStyle: 'map' | 'story' | 'map3d',
  ): Observable<any> {
    return this._apiService.put(`hubs/${hubName}/tours/update-details-style-for-all`, {
      detailsStyle,
    });
  }

  updateMapStyleForAll(hubName: string, mapStyle: 'light' | 'dark'): Observable<any> {
    return this._apiService.put(`hubs/${hubName}/tours/update-map-style-for-all`, { mapStyle });
  }

  updateItineraries(hubName: string, tours: any): Observable<any[]> {
    return this._apiService.put(`hubs/${hubName}/tours`, tours);
  }

  convertToTravelTimeText(totalTime: number): string {
    return this._eventTimeService.convertToTravelTimeText(totalTime);
  }

  convertToTravelDistance(distance: number): {
    miles: number;
    km: number;
    all: string;
  } {
    if (!Number.isFinite(distance)) {
      return { miles: 0, km: 0, all: '' };
    }
    const miles = Math.round(distance / 1610);
    const km = Math.round(distance / 1000);
    return {
      miles,
      km,
      all: `${miles} miles / ${km} km`,
    };
  }

  sendEmails(
    emailAddresses: string[],
    tripUrl: string,
    html?: string,
    hubName?: string,
    presentation?: {
      type: string;
      title: string;
      userName: string;
      profilePicture: string;
      cityName: string;
    },
  ): Observable<any> {
    return this._apiService.post(`common/send-emails`, {
      emailAddresses,
      tripUrl,
      html,
      hubName,
      presentation,
    });
  }

  public getUserTripsV3(hubName: string, userFacebookId: string): Promise<TripStorageCommand> {
    return lastValueFrom(
      this._apiService.get(`visitors/${hubName}/trips/user-v3/${userFacebookId}/user`).pipe(
        map((data: TripStorageCommand) => {
          if (!isEmptyArray(data.trips)) {
            data.trips.forEach((trip) => {
              trip.travelMode = trip.travelMode || TravelMode.DRIVING;

              trip.days.forEach((day) => {
                let array = [];
                day.attractions &&
                  day.attractions.forEach((x) => {
                    if (x.status.toLowerCase() === 'ok') {
                      if (x.feedType === 'event') {
                        this._eventTimeService.processEventTimes(x.item as VisitorEvent);
                      } else {
                        if (!x.item || !x.item.address) return;
                        x.item.address = {
                          address: x.item.address.toString(),
                          lat: x.item.lat,
                          lng: x.item.lng,
                        };
                        (x.item as VisitorPlace).categoryText =
                          this._visitorService.getDisplayCategory(x.item as VisitorPlace);
                      }

                      array.push(x);
                    }
                  });
                day.attractions = array;
              });
            });
          }
          return data;
        }),
      ),
    );
  }

  public getUserSingleTripsV3(
    hubName: string,
    entireTripId: string,
    singleTripId: string,
  ): Promise<UserTrip> {
    return lastValueFrom(
      this._apiService
        .get(`visitors/${hubName}/trips/single-v3/${entireTripId}/${singleTripId}`)
        .pipe(
          map((data: UserTrip) => {
            if (!isEmptyArray(data.days)) {
              data.days.forEach((day) => {
                let array = [];
                day.attractions &&
                  day.attractions.forEach((x) => {
                    if (x.status.toLowerCase() === 'ok') {
                      if (x.feedType === 'event') {
                        this._eventTimeService.processEventTimes(x.item as VisitorEvent);
                      } else {
                        if (!x.item || !x.item.address) return;
                        x.item.address = {
                          address: x.item.address.toString(),
                          lat: x.item.lat,
                          lng: x.item.lng,
                        };
                        (x.item as VisitorPlace).categoryText =
                          this._visitorService.getDisplayCategory(x.item as VisitorPlace);
                      }

                      array.push(x);
                    }
                  });
                day.attractions = array;
              });
            }
            return data;
          }),
        ),
    );
  }

  /**
   * Validate if an event exists in a specific itinerary on the server side
   * @param hubName - The hub name
   * @param itineraryId - The itinerary ID
   * @param eventId - The event ID to validate
   * @returns Observable of the event if found, null if not found
   */
  public validateEventInItinerary(
    hubName: string,
    itineraryId: string,
    eventId: string,
  ): Observable<VisitorEvent | null> {
    return this._apiService
      .get(`visitors/${hubName}/trips/${itineraryId}/validate-event/${eventId}`)
      .pipe(
        map((data: any) => {
          // If the API returns the event data, return it
          // If the API returns null/undefined, the event doesn't exist in the itinerary
          return data || null;
        }),
      );
  }

  /**
   * Validate if a place exists in a specific itinerary on the server side
   * @param hubName - The hub name
   * @param itineraryId - The itinerary ID
   * @param placeId - The place ID to validate
   * @returns Observable of the place if found, null if not found
   */
  public validatePlaceInItinerary(
    hubName: string,
    itineraryId: string,
    placeId: string,
  ): Observable<VisitorPlace | null> {
    return this._apiService
      .get(`visitors/${hubName}/trips/${itineraryId}/validate-place/${placeId}`)
      .pipe(
        map((data: any) => {
          // If the API returns the place data, return it
          // If the API returns null/undefined, the place doesn't exist in the itinerary
          return data || null;
        }),
      );
  }
}
